import { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useTransform, AnimatePresence } from "framer-motion";
import { Heart, X, Star, MapPin, Sparkles, Info, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { tgHapticImpact, tgHapticNotification } from "../lib/telegram";
import BottomNav from "../components/BottomNav";
import MatchModal from "../components/MatchModal";
import PremiumBadge from "../components/PremiumBadge";
import { t } from "../lib/i18n";

function ProfileCard({ profile, style, onTap, onStoryTap }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const photos = profile.photos || [];
  const current = photos[photoIdx];

  const changePhoto = (dir) => {
    setPhotoIdx((i) => Math.max(0, Math.min(photos.length - 1, i + dir)));
  };

  return (
    <motion.div
      className="swipe-card absolute inset-0"
      style={style}
      data-testid={`profile-card-${profile.id}`}
    >
      {/* Photo carousel */}
      <div className="relative h-[68%] w-full bg-[color:var(--peach-bg)] overflow-hidden">
        {current && (
          <img
            src={current.data_url}
            alt=""
            className={`w-full h-full object-cover ${current.blurred ? "photo-blur" : ""}`}
            draggable={false}
          />
        )}

        {/* Story ring (top-right) */}
        {profile.has_story && (
          <button
            data-testid={`story-ring-${profile.id}`}
            onClick={(e) => { e.stopPropagation(); onStoryTap?.(profile.id); }}
            className="absolute top-3 right-3 z-30 p-[3px] rounded-full bg-gradient-to-tr from-[#E76F51] via-[#F4A261] to-[#E9C46A] active:scale-95 transition-transform"
            aria-label="View story"
          >
            <div className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E76F51" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 7l-7 5 7 5V7z" />
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
              </svg>
            </div>
          </button>
        )}

        {/* Progress dots */}
        {photos.length > 1 && (
          <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
            {photos.map((_, i) => (
              <div
                key={i}
                className={`flex-1 h-1 rounded-full ${
                  i === photoIdx ? "bg-white" : "bg-white/40"
                }`}
              />
            ))}
          </div>
        )}
        {/* Tap zones for photo nav */}
        <button
          className="absolute left-0 top-0 w-1/3 h-full z-10"
          onClick={(e) => { e.stopPropagation(); changePhoto(-1); }}
          aria-label="prev photo"
        />
        <button
          className="absolute right-0 top-0 w-1/3 h-full z-10"
          onClick={(e) => { e.stopPropagation(); changePhoto(1); }}
          aria-label="next photo"
        />

        {/* Blur CTA */}
        {current?.blurred && (
          <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
            <div className="glass rounded-3xl px-5 py-4 text-center max-w-[80%] pointer-events-auto">
              <Sparkles className="w-6 h-6 text-[color:var(--peach-deep)] mx-auto mb-2" />
              <div className="text-sm font-semibold text-[color:var(--ink)]">
                Загрузи ещё фото, чтобы увидеть все
              </div>
            </div>
          </div>
        )}

        {/* Gradient overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
        <div className="absolute bottom-4 left-5 right-5 text-white z-10">
          <div className="flex items-end gap-2">
            <div className="font-[Onest] font-extrabold text-[32px] leading-tight">
              {profile.first_name}
            </div>
            {profile.age != null ? (
              <div className="font-[Onest] font-semibold text-2xl opacity-90">{profile.age}</div>
            ) : null}
            {profile.is_premium && <PremiumBadge variant="pill" className="mb-1.5" />}
          </div>
          <div className="flex items-center gap-2 mt-1 text-[13px]">
            <MapPin className="w-3.5 h-3.5" />
            <span>
              {profile.city}
              {profile.distance_km != null ? ` · ${profile.distance_km} км` : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Bio */}
      <div className="h-[32%] p-5 flex flex-col bg-[color:var(--peach-card)] relative z-[1]">
        <div className="text-[color:var(--ink)] text-[15px] leading-relaxed line-clamp-2 flex-1 min-h-0">
          {profile.bio || <span className="text-[color:var(--ink-soft)] italic">Без описания</span>}
        </div>
        {/* Username + "Подробнее" share one fixed-height row so the button's
            position never shifts (and never collides with the floating swipe
            buttons below) depending on whether a premium viewer sees a username. */}
        <div className="mt-2 flex items-center justify-between gap-2">
          {profile.username_visible ? (
            <div className="text-xs text-[color:var(--peach-deep)] font-semibold truncate">
              @{profile.username_visible}
            </div>
          ) : <span />}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onTap?.();
            }}
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/80 border border-[color:var(--border)] text-[color:var(--ink)]"
          >
            <Info className="w-3.5 h-3.5" /> Подробнее
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function TopCard({ profile, onSwipe, onStoryTap, onTap }) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-18, 18]);
  const likeOp = useTransform(x, [30, 140], [0, 1]);
  const nopeOp = useTransform(x, [-140, -30], [1, 0]);

  const handleDragEnd = (_, info) => {
    const off = info.offset.x;
    const vel = info.velocity.x;
    if (off > 120 || vel > 500) onSwipe("like");
    else if (off < -120 || vel < -500) onSwipe("skip");
  };

  return (
    <motion.div
      className="absolute inset-0 z-10"
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      style={{ x, rotate }}
      onDragEnd={handleDragEnd}
      whileTap={{ cursor: "grabbing" }}
    >
      <ProfileCard profile={profile} onStoryTap={onStoryTap} onTap={onTap} />

      {/* Like/Nope stamps */}
      <motion.div
        style={{ opacity: likeOp }}
        className="absolute top-8 right-6 px-4 py-2 rounded-2xl border-4 border-[color:var(--peach-deep)] text-[color:var(--peach-deep)] font-[Onest] font-extrabold text-3xl rotate-[-15deg] pointer-events-none"
      >
        ЛАЙК
      </motion.div>
      <motion.div
        style={{ opacity: nopeOp }}
        className="absolute top-8 left-6 px-4 py-2 rounded-2xl border-4 border-[color:var(--ink)] text-[color:var(--ink)] font-[Onest] font-extrabold text-3xl rotate-[15deg] pointer-events-none"
      >
        НЕТ
      </motion.div>
    </motion.div>
  );
}

export default function Discover() {
  const { user, refresh } = useAuth();
  const lang = user?.language || "ru";
  const nav = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exhausted, setExhausted] = useState(false); // a fetch genuinely returned no one new
  const [matchModal, setMatchModal] = useState(null); // matched user public
  const [lastMatchId, setLastMatchId] = useState(null);
  const [swiping, setSwiping] = useState(false);
  const [superlikeOpen, setSuperlikeOpen] = useState(false);
  const [superlikeMsg, setSuperlikeMsg] = useState("");
  const fetchingRef = useRef(false);

  const load = async (initial = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (initial) setLoading(true);
    try {
      const { data } = await api.get("/feed");
      if (initial) {
        setCards(data);
        setExhausted(data.length === 0);
      } else {
        setCards((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const merged = [...prev, ...data.filter((p) => !seen.has(p.id))];
          setExhausted(merged.length === 0);
          return merged;
        });
      }
    } catch (e) {
      if (initial) toast.error(t(lang, "err_load_feed"));
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    // Always try to refresh location silently on mount
    if (user && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            await api.post("/profile/location", {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            });
            // Silently refresh user data in background
            refresh();
          } catch {}
          load(true);
        },
        () => load(true),
        { timeout: 6000, enableHighAccuracy: true }
      );
    } else {
      load(true);
    }
    // eslint-disable-next-line
  }, []);

  // Top up the buffer well before it runs out. It used to only check *after*
  // a swipe, against the pre-swipe count, with an extra 400ms delay before
  // even starting the fetch — a fast swiper could burn through the last few
  // cards before that fetch landed, hit cards.length === 0, and fall straight
  // into the "no more profiles" dead-end even though plenty of people were
  // still left to load. This re-checks on every buffer change instead.
  useEffect(() => {
    if (!loading && !exhausted && cards.length <= 6) {
      load(false);
    }
    // eslint-disable-next-line
  }, [cards.length, loading, exhausted]);

  const act = async (type, message) => {
    if (swiping || cards.length === 0) return;
    setSwiping(true);
    const top = cards[0];
    setCards((c) => c.slice(1));
    try {
      const { data } = await api.post("/likes", { to_user_id: top.id, type, message: message || undefined });
      if (data.matched) {
        tgHapticNotification("success");
        setMatchModal(data.matched_user);
        setLastMatchId(data.match?.id);
      } else {
        tgHapticImpact("light");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || t(lang, "error_generic");
      toast.error(msg);
    } finally {
      setSwiping(false);
    }
  };

  const openSuperlikeModal = () => {
    if (swiping || cards.length === 0) return;
    setSuperlikeMsg("");
    setSuperlikeOpen(true);
  };

  const submitSuperlike = () => {
    setSuperlikeOpen(false);
    act("superlike", superlikeMsg.trim() || "Хотел(а) с тобой познакомиться! 💫");
  };

  const openChat = () => {
    if (lastMatchId) {
      setMatchModal(null);
      nav(`/chat/${lastMatchId}`);
    }
  };

  return (
    <div className="kupidon-shell safe-top flex flex-col relative" data-testid="discover-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between z-20">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-soft)]">{t(lang, "discover_kicker")}</div>
          <div className="font-[Onest] font-extrabold text-2xl -mt-0.5">{t(lang, "discover_title")}</div>
        </div>
        <div className="w-10 h-10 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-[color:var(--peach-deep)]" />
        </div>
      </div>

      {/* Card stack — bottom margin must clear the floating action buttons
          (bottom-24, ~56px tall, so they occupy ~96-152px from the shell's
          bottom edge) or the card's own bottom content (username/"Подробнее")
          visually collides with them. mb-28 (112px) left a ~40px overlap. */}
      <div className="flex-1 relative mx-4 mb-44 mt-2" data-testid="card-stack">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-[color:var(--ink-soft)]">{t(lang, "loading")}</div>
          </div>
        )}
        {/* Buffer momentarily empty but a background top-up is in flight
            (e.g. a fast swiper outran the fetch) — read as "loading", not
            "no one's left", since a genuine dead-end hasn't been confirmed yet. */}
        {!loading && cards.length === 0 && !exhausted && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-[color:var(--ink-soft)]">{t(lang, "loading")}</div>
          </div>
        )}
        {!loading && cards.length === 0 && exhausted && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
            <div className="w-24 h-24 rounded-full bg-[color:var(--peach)]/15 flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-[color:var(--peach-deep)]" strokeWidth={2} />
            </div>
            <div className="font-[Onest] font-bold text-xl">{t(lang, "no_more_title")}</div>
            <div className="text-sm text-[color:var(--ink-soft)] mt-2 max-w-[26ch]">
              {t(lang, "no_more_sub")}
            </div>
            <button
              onClick={() => { setExhausted(false); load(true); }}
              className="btn-pill mt-6 bg-[color:var(--peach)] text-white"
              data-testid="discover-reload"
            >
              {t(lang, "refresh")}
            </button>
          </div>
        )}

        {/* Bottom static ghost cards — plain peach placeholders, no photos, no text */}
        {cards.slice(1, 3).map((p, i) => (
          <div
            key={p.id}
            className="swipe-card absolute inset-0 pointer-events-none"
            style={{
              transform: `scale(${1 - (i + 1) * 0.05}) translateY(${(i + 1) * 10}px)`,
              zIndex: 1 + (2 - i),
              background: "linear-gradient(180deg, #FFE9DA 0%, #FFDAC1 100%)",
              opacity: 0.85 - i * 0.2,
            }}
            aria-hidden
          />
        ))}

        <AnimatePresence mode="popLayout">
          {cards.length > 0 && (
            <TopCard
              key={cards[0].id}
              profile={cards[0]}
              onSwipe={act}
              onStoryTap={(uid) => nav(`/story/${uid}`)}
              onTap={() => {
                nav(`/person/${cards[0].id}`, {
                  state: { profile: cards[0], backTo: "/discover" },
                });
              }}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Action buttons */}
      {!loading && cards.length > 0 && (
        <div className="absolute bottom-24 left-0 right-0 flex items-center justify-center gap-5 z-20">
          <button
            data-testid="skip-button"
            onClick={() => act("skip")}
            disabled={swiping}
            className="w-14 h-14 rounded-full bg-white shadow-lg shadow-[#1F1A17]/10 flex items-center justify-center border border-[color:var(--border)] active:scale-90 transition-transform"
          >
            <X className="w-6 h-6 text-[color:var(--ink)]" strokeWidth={2.5} />
          </button>
          <button
            data-testid="superlike-button"
            onClick={openSuperlikeModal}
            disabled={swiping}
            className="w-12 h-12 rounded-full bg-[color:var(--teal-superlike)] shadow-lg shadow-[#2A9D8F]/30 flex items-center justify-center active:scale-90 transition-transform"
          >
            <Star className="w-5 h-5 text-white" fill="white" strokeWidth={0} />
          </button>
          <button
            data-testid="like-button"
            onClick={() => act("like")}
            disabled={swiping}
            className="w-14 h-14 rounded-full bg-gradient-to-br from-[#F4A261] to-[#E76F51] shadow-lg shadow-[#E76F51]/40 flex items-center justify-center active:scale-90 transition-transform"
          >
            <Heart className="w-7 h-7 text-white" fill="white" strokeWidth={0} />
          </button>
        </div>
      )}

      <BottomNav />

      {/* Superlike modal */}
      <AnimatePresence>
        {superlikeOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            onClick={() => setSuperlikeOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.3 }}
              className="w-full max-w-sm rounded-3xl bg-white border border-[color:var(--border)] p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-[color:var(--teal-superlike)]/15 flex items-center justify-center">
                  <Star className="w-6 h-6 text-[color:var(--teal-superlike)]" fill="currentColor" />
                </div>
                <div>
                  <div className="font-[Onest] font-bold text-lg">Суперлайк</div>
                  <div className="text-xs text-[color:var(--ink-soft)]">
                    {cards[0]?.first_name || "пользователь"} сразу увидит вас в мэтчах
                  </div>
                </div>
              </div>

              <textarea
                value={superlikeMsg}
                onChange={(e) => setSuperlikeMsg(e.target.value.slice(0, 50))}
                placeholder="Напишите что-нибудь приятное (до 50 символов)..."
                rows={2}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] text-sm resize-none mb-2"
              />
              <div className="text-[10px] text-[color:var(--ink-soft)] text-right -mt-1 mb-3">{superlikeMsg.length}/50</div>

              <div className="flex gap-2">
                <button
                  onClick={() => setSuperlikeOpen(false)}
                  className="flex-1 py-2.5 rounded-full bg-white border border-[color:var(--border)] text-sm font-medium"
                >
                  Отмена
                </button>
                <button
                  onClick={submitSuperlike}
                  className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-[color:var(--teal-superlike)] to-[color:var(--teal-superlike)] text-white text-sm font-semibold flex items-center justify-center gap-1.5 shadow-md"
                >
                  <Send className="w-3.5 h-3.5" /> Отправить
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <MatchModal
        open={!!matchModal}
        matched={matchModal}
        myFirstName={user?.first_name}
        onSendMessage={openChat}
        onKeepSwiping={() => setMatchModal(null)}
      />
    </div>
  );
}
